/**
 * @fileoverview Defines functions `walkFolder` and `walkFolderAsync` for recursively traversing a directory structure. It uses the `Ignorer` class to filter files and folders during traversal. The functions accept a path and a handler function to process each file and folder found.
 */

import { baseOfSparsePath, SparsePath } from './sparse_path'
import { Ignorer } from './ignore'
import * as fs from 'fs'
import * as pathlib from 'path'
import { DirectoryIgnorer } from './ignore'

/**
 * Walks a folder and its subfolders, calling a handler function for each file and folder. Allows
 * for custom ignorers to skip files. By default will respect .awignore and .gitignore as well as
 * ignore .awolf directories and non-text files.
 *
 * @param folderPath - Path to the root folder to walk
 * @param handleFile - Function to handle each file or folder
 */
export async function walkFolder(
  folderPath: SparsePath,
  handleFile: (path: string, type: 'file' | 'folder') => void,
  ignorer: Ignorer = new DirectoryIgnorer(folderPath),
) {
  const recursiveWalk = (currentPath: string) => {
    const fileAndDirs = fs.readdirSync(currentPath)
    const folderPaths = []

    for (const fileOrDir of fileAndDirs) {
      const fullPath = pathlib.join(currentPath, fileOrDir)
      if (ignorer.shouldSkip(fullPath)) {
        continue
      }

      const stats = fs.statSync(fullPath)
      if (stats.isFile()) {
        handleFile(fullPath, 'file')
      } else if (stats.isDirectory()) {
        folderPaths.push(fullPath)
        handleFile(fullPath, 'folder')
      }
    }

    for (const path of folderPaths) {
      recursiveWalk(path)
    }
  }

  recursiveWalk(baseOfSparsePath(folderPath))
}

/**
 * Walks a folder and its subfolders, calling a handler function for each file and folder. Allows
 * for custom ignorers to skip files. By default will respect .awignore and .gitignore as well as
 * ignore .awolf directories and non-text files.
 *
 * @param folderPath - Path to the root folder to walk or SparseDirectory object
 * @param handleFile - Function to handle each file or folder
 */
export async function walkFolderAsync(
  folderPath: string | SparsePath,
  handleFile: (path: string, type: 'file' | 'folder') => Promise<void>,
  ignorer: Ignorer = new DirectoryIgnorer(folderPath),
) {
  const recursiveWalk = async (currentPath: string) => {
    const fileAndDirs = fs.promises.readdir(currentPath)
    const folderPaths = []

    for (const fileOrDir of await fileAndDirs) {
      const fullPath = pathlib.join(currentPath, fileOrDir)
      if (ignorer.shouldSkip(fullPath)) {
        continue
      }

      const stats = await fs.promises.stat(fullPath)
      if (stats.isFile()) {
        await handleFile(fullPath, 'file')
      } else if (stats.isDirectory()) {
        folderPaths.push(fullPath)
        await handleFile(fullPath, 'folder')
      }
    }

    for (const path of folderPaths) {
      await recursiveWalk(path)
    }
  }

  await recursiveWalk(baseOfSparsePath(folderPath))
}
